import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOeuvres, type OeuvrePresentee } from '../../api/oeuvres.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import styles from './RechercheContact.module.css';

export interface RechercheOeuvreProps {
  galerieSlug: string;
  onChoisir: (oeuvre: OeuvrePresentee) => void;
}

/**
 * Recherche d'une œuvre de l'inventaire pour une ligne de facture (§ 17.4) :
 * la ligne emprunte à la fiche sa désignation, son prix, son régime de TVA et
 * son droit de suite (`apps/api/src/ventes/lignes.ts`, `construireLigne`) —
 * le choix se fait ici, le reste vient de l'API à la création de la ligne.
 */
export function RechercheOeuvre({ galerieSlug, onChoisir }: RechercheOeuvreProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);

  const resultats = useOeuvres(galerieSlug, {
    q: rechercheDebattue || undefined,
    parPage: 8,
  });

  return (
    <div className={styles.recherche}>
      <input
        type="search"
        className={styles.saisie}
        placeholder={t('regie:ventes.fiche.rechercheOeuvre')}
        aria-label={t('regie:ventes.fiche.rechercheOeuvre')}
        value={recherche}
        onChange={(event) => setRecherche(event.target.value)}
      />
      {rechercheDebattue.trim().length > 0 ? (
        <div className={styles.resultats}>
          {resultats.isLoading ? (
            <p className={styles.vide}>{t('commun:chargement')}</p>
          ) : (resultats.data?.oeuvres.length ?? 0) === 0 ? (
            <p className={styles.vide}>{t('regie:ventes.fiche.rechercheOeuvreAucunResultat')}</p>
          ) : (
            resultats.data?.oeuvres.map((oeuvre) => (
              <button
                key={oeuvre.id}
                type="button"
                className={styles.ligne}
                onClick={() => {
                  onChoisir(oeuvre);
                  setRecherche('');
                }}
              >
                <span>
                  {oeuvre.artisteNom} — {oeuvre.titre}
                </span>
                <span className={styles.ligneMeta}>{oeuvre.numeroInventaire}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
